'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { Search, ArrowRight, Lightbulb, TrendingUp, Users, Briefcase } from 'lucide-react';
import { useRouter } from 'next/navigation';

const TRENDING_TAGS = [
  'Clean Energy', 'Rural Healthcare', 'Supply Chain', 'EdTech', 'AgriTech', 'Fintech',
];

export default function HeroSection() {
  const [query, setQuery] = useState('');
  const router = useRouter();

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    if (query.trim()) {
      router.push(`/problem-solution-board?q=${encodeURIComponent(query.trim())}`);
    }
  }

  return (
    <section className="gradient-hero blob-primary py-20 lg:py-28 xl:py-32 overflow-hidden">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 2xl:px-16">
        <div className="max-w-4xl mx-auto text-center">
          {/* Headline */}
          <h1 className="text-hero-xl font-extrabold text-foreground mb-5 tracking-tight">
            Real Problems.{' '}
            <span className="text-primary">Bold Solutions.</span>
            <br />
            Serious Capital.
          </h1>

          {/* Sub */}
          <p className="text-hero-sub text-muted-foreground mb-10 max-w-2xl mx-auto">
            Monezite bridges the gap between real-world challenges, entrepreneurial ingenuity, and investor capital — all in one open innovation platform.
          </p>

          {/* Search */}
          <form onSubmit={handleSearch} className="relative max-w-2xl mx-auto mb-6">
            <div className="flex items-center bg-card border-2 border-border focus-within:border-primary rounded-xl card-shadow-md transition-colors duration-150 overflow-hidden">
              <Search size={20} className="ml-4 text-muted-foreground shrink-0" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Problems, Solutions"
                className="flex-1 px-3 py-4 sm:px-4 bg-transparent text-foreground placeholder:text-muted-foreground outline-none text-sm sm:text-base font-medium min-w-0"
              />
              <button
                type="submit"
                className="m-1.5 px-3 py-2 sm:px-5 sm:py-2.5 bg-primary text-primary-foreground text-xs sm:text-sm font-semibold rounded-lg hover:bg-cyan-600 active:scale-95 transition-all duration-150 shrink-0 whitespace-nowrap"
              >
                Search
              </button>
            </div>
          </form>

          {/* Trending Tags */}
          <div className="flex flex-wrap items-center justify-center gap-2 mb-12">
            <span className="text-xs font-medium text-muted-foreground">Trending:</span>
            {TRENDING_TAGS.map((tag) => (
              <Link
                key={`trending-${tag}`}
                href={`/problem-solution-board?q=${encodeURIComponent(tag)}`}
                className="text-xs font-semibold text-primary bg-primary/10 hover:bg-primary/20 px-3 py-1 rounded-full transition-colors duration-150"
              >
                {tag}
              </Link>
            ))}
          </div>

          {/* Role CTAs */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {[
              {
                icon: Lightbulb,
                title: 'Post a Problem',
                desc: 'Share a real-world challenge and attract entrepreneurs',
                color: 'text-orange-500',
                bg: 'bg-orange-50 hover:bg-orange-100 border-orange-200',
                href: '/post-problem',
              },
              {
                icon: TrendingUp,
                title: 'Propose a Solution',
                desc: 'Browse problems and pitch your entrepreneurial ideas',
                color: 'text-violet-500',
                bg: 'bg-violet-50 hover:bg-violet-100 border-violet-200',
                href: '/problem-solution-board',
              },
              {
                icon: Users,
                title: 'Fund a Deal',
                desc: 'Discover promising solutions and submit funding offers',
                color: 'text-primary',
                bg: 'bg-primary/5 hover:bg-primary/10 border-primary/20',
                href: '/sign-up-login-screen?mode=signup&role=investor',
              },
              {
                icon: Briefcase,
                title: 'Hire or Get Hired',
                desc: 'Employers hire specialists · Job seekers apply for roles',
                color: 'text-emerald-600',
                bg: 'bg-emerald-50 hover:bg-emerald-100 border-emerald-200',
                href: '/workers',
              },
            ].map((item) => (
              <Link
                key={`hero-cta-${item.title}`}
                href={item.href}
                className={`group flex flex-col items-center gap-3 p-5 rounded-xl border ${item.bg} transition-all duration-200 card-shadow hover:card-shadow-md`}
              >
                <item.icon size={24} className={item.color} />
                <div>
                  <p className="text-sm font-bold text-foreground mb-0.5">{item.title}</p>
                  <p className="text-xs text-muted-foreground">{item.desc}</p>
                </div>
                <ArrowRight size={14} className={`${item.color} opacity-0 group-hover:opacity-100 transition-opacity duration-150`} />
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}